import type { SceneryArrays, ZoneBuilder } from '../types';
import { scatterPeople } from '../crowd';
import { hillLat } from '../hills';
import {
  BOAT_COLORS,
  DAKAR_DOORS,
  DAKAR_RENDER,
  DAKAR_SAND,
  PINK_LAKE,
  UMBRELLA_COLORS,
  darken,
} from '../palettes';

/**
 * scenery/zones/dakar.ts — Sahel haze and rally soul. Whitewashed flat-roofed
 * medina with bright doorways → the parasol bustle of a market street → open
 * baobab savanna with dunes (and the dune jump) → the famous PINK lake with
 * its salt mounds and pirogues. The lake lies on side +1.
 */

export const LAKE_SIDE = 1;

/** The Gorée house stands on side +1 (cities.ts `landmarks`). Its footprint is
 *  MEASURED (live bbox → track coords): course distance 911-932, lateral
 *  12-44 — i.e. it sits squarely on the band the market stalls occupy. */
const GOREE_SIDE = 1;
/** Half-window (in progress) of the Gorée forecourt clearing. 0.008 × the
 *  2192-unit course = ±17.5 units, so the three placement steps that touch the
 *  house (906 / 918 / 930) are covered with a step of margin either side. */
const GOREE_CLEAR = 0.008;

/** Per-instance variety WITHOUT touching the layout rng (the M36 index-hash
 *  rule — the main stream's DRAW ORDER is the city's identity). */
function hash01(i: number): number {
  let h = Math.imul(i ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Sacks and produce crates stacked under a market counter. */
const GOODS = [0x9a6a34, 0xb08a44, 0x7a5230, 0xa8552c, 0x8a7a3a];

const POST = 0x5b4026;

/**
 * The stall's own LEGS and GOODS (dakar-ts). `buildStalls` draws a counter box
 * and a tilted awning floating 2.3 up with NOTHING in between, so from two car
 * lengths away the canopy reads as a coloured plank hovering over the sand
 * (measured in the market chase + orbit shots). Four corner posts tie the
 * canopy down and a pair of goods crates give the counter something to sell.
 *
 * Everything lands in `a.plain` — the same InstancedMesh the market's existing
 * crates use, so this is zero new draw calls — and every value here is either
 * fixed or index-hashed: NOT ONE rng draw, so the city's layout stream (and
 * therefore every other placement in Dakar) is byte-identical.
 *
 * `fx/fz` is the course-forward unit vector (§6: heading h → (sin h, cos h));
 * `at()` only walks sideways, so along-course offsets are added by hand.
 */
function dressStall(
  a: SceneryArrays,
  at: (lateral: number) => [number, number, number],
  lat: number,
  dir: number,
  fx: number,
  fz: number,
  idx: number,
): void {
  // Corner posts, under the awning's 3.0 × 2.8 footprint (buildStalls).
  // MEASURED: the awning is TILTED 0.16 rad about the WORLD x-axis (buildStalls
  // sets the euler before the yaw), so its underside sits anywhere in 2.0-2.44
  // depending which way the stall faces. Posts run to 2.44 — always touching,
  // never re-opening the floating-canopy gap; the little that projects past the
  // cloth reads as a pole head, which is how a real stall is rigged.
  for (const dl of [-1.32, 1.32]) {
    const [px, py, pz] = at(lat + dl);
    for (const df of [-1.22, 1.22]) {
      a.plain.push({
        x: px + fx * df,
        y: py,
        z: pz + fz * df,
        rotY: dir,
        w: 0.17,
        h: 2.44,
        d: 0.17,
        color: POST,
      });
    }
  }
  // Two crates propped against the counter — one road-side, one behind.
  const [ax, ay, az] = at(lat - 1.55);
  a.plain.push({
    x: ax + fx * 0.75,
    y: ay,
    z: az + fz * 0.75,
    rotY: dir + 0.22,
    w: 0.82,
    h: 0.62,
    d: 0.82,
    color: GOODS[(hash01(idx) * GOODS.length) | 0],
  });
  const [bx, by, bz] = at(lat + 1.5);
  a.plain.push({
    x: bx - fx * 0.85,
    y: by,
    z: bz - fz * 0.85,
    rotY: dir - 0.3,
    w: 0.68,
    h: 0.52,
    d: 0.68,
    color: GOODS[(hash01(idx * 7 + 3) * GOODS.length) | 0],
  });
}

const medina: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a }) => {
  // Flat-roofed render blocks with a parapet lip + a bright doorway.
  if (rng.next() < 0.86) {
    const w = rng.range(8, 13);
    const h = rng.range(4.5, 9);
    const d = rng.range(8, 13);
    const lat = hw + 3 + w / 2 + rng.range(0, 5);
    const [x, y, z] = at(lat);
    const color = rng.pick(DAKAR_RENDER);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.3, z, rotY: s.dir, w: w + 0.6, h: 0.7, d: d + 0.6, color: darken(color, 0.78) });
    const [dx, dy, dz] = at(lat - w / 2 - 0.15);
    a.plain.push({ x: dx, y: dy, z: dz, rotY: s.dir, w: 0.35, h: 3.2, d: 1.8, color: rng.pick(DAKAR_DOORS) });
    // The medina signatures (M14): a woven-mat AWNING shading the doorway,
    // stair-stepped parapet corners, and the rooftop life every Sahel block
    // carries (a water drum, a line of drying cloth).
    a.plain.push({ x: dx, y: dy + 3.6, z: dz, rotY: s.dir, w: 1.6, h: 0.16, d: 2.6, color: 0xc8a868 }); // mat awning
    const [pc1x, pc1y, pc1z] = at(lat + w / 2 - 0.6);
    a.plain.push({ x: pc1x, y: pc1y + h + 0.45, z: pc1z, rotY: s.dir, w: 1.1, h: 0.9, d: 1.1, color: darken(color, 0.82) }); // stepped corner
    if (rng.next() < 0.5) {
      const [rx, ry, rz] = at(lat + rng.range(-1.5, 1.5));
      a.plain.push({ x: rx, y: ry + h + 0.55, z: rz, rotY: s.dir, w: 1.0, h: 1.1, d: 1.0, color: 0x3a6a8a }); // water drum
      a.plain.push({ x: rx, y: ry + h + 0.8, z: rz, rotY: s.dir + 0.4, w: 2.8, h: 0.5, d: 0.12, color: rng.pick(DAKAR_DOORS) }); // drying cloth
    }
  }
  if (rng.next() < 0.12) {
    const [x, y, z] = at(hw + 2.4);
    a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.8, 1.1) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.5, hw + 4], chance: 0.55, count: 3 });
};

const market: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, progress, marks }) => {
  // GORÉE FORECOURT (dakar-ts). The Gorée house occupies lateral 12-44 over
  // course distance 911-932 (measured) — exactly the band the stall rail and
  // the parasols sit on, so a yellow canopy plank grew through its front
  // elevation. DISCARD, DON'T SKIP: every rng draw below still happens (an
  // early `return` would shift the main stream and reshuffle the rest of the
  // city) — only the PUSH is withheld inside the window. Crowds deliberately
  // survive: people at a landmark are the point (the M97 finishClear rule).
  const goreeAt = marks.goreeHouse;
  const clear = goreeAt != null && side === GOREE_SIDE && Math.abs(progress - goreeAt) < GOREE_CLEAR;
  const fx = Math.sin(s.dir);
  const fz = Math.cos(s.dir);
  // Stall after stall under bright awnings, parasols between, goods crates.
  if (rng.next() < 0.6) {
    const lat = hw + 2.6 + rng.range(0, 2);
    const [x, y, z] = at(lat);
    const color = rng.pick(UMBRELLA_COLORS);
    if (!clear) {
      dressStall(a, at, lat, s.dir, fx, fz, a.stalls.length);
      a.stalls.push({ x, y, z, rotY: s.dir, color });
    }
  }
  if (rng.next() < 0.4) {
    const [x, y, z] = at(hw + 4.5 + rng.range(0, 3));
    const su = rng.range(1.0, 1.35);
    const color = rng.pick(UMBRELLA_COLORS);
    if (!clear) a.umbrellas.push({ x, y, z, rotY: 0, s: su, color });
  }
  if (rng.next() < 0.45) {
    const [x, y, z] = at(hw + 2.2 + rng.range(0, 1.5));
    const rot = s.dir + rng.range(-0.4, 0.4);
    const w = rng.range(0.8, 1.4);
    const h = rng.range(0.6, 1.2);
    const d = rng.range(0.8, 1.4);
    const color = rng.pick(DAKAR_DOORS);
    if (!clear) a.plain.push({ x, y, z, rotY: rot, w, h, d, color });
  }
  // Low blocks looking over the bustle.
  if (rng.next() < 0.55) {
    const w = rng.range(9, 14);
    const h = rng.range(5, 10);
    const [x, y, z] = at(hw + 9 + w / 2 + rng.range(0, 4));
    const d = rng.range(9, 13);
    const color = rng.pick(DAKAR_RENDER);
    if (!clear) a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
  }
  // The market throng, thick between the stalls.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.5, hw + 6], chance: 0.85, count: 5 });
};

const savanna: ZoneBuilder = ({ rng, hw, at, a }) => {
  // The themed ground already reads as open sand — just populate it.
  // Baobabs — the giants of the Sahel.
  if (rng.next() < 0.2) {
    const [x, y, z] = at(hw + 8 + rng.range(0, 36));
    a.baobabs.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.8, 1.5) });
  }
  // Dry shrubs.
  if (rng.next() < 0.3) {
    const [x, y, z] = at(hw + 4 + rng.range(0, 30));
    a.trees.push({ x, y, z, s: rng.range(0.5, 0.8), color: rng.pick([0x8a8a4a, 0x9a9456, 0x7a7a40]) });
  }
  // Rolling dunes set back (M98: edge clamped off the road, same draws).
  if (rng.next() < 0.3) {
    const base = hw + 24 + rng.range(0, 40);
    const r = rng.range(18, 38);
    const [x, y, z] = at(hillLat(hw, base, r));
    a.hills.push({ x, y, z, r, h: rng.range(4, 10), color: rng.pick(DAKAR_SAND) });
  }
};

const lacrose: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  if (side === LAKE_SIDE) {
    // The pink lake itself, with salt mounds on the shore + pirogues out on it.
    {
      const w = rng.range(55, 75);
      const [x, y, z] = at(hw + 12 + w / 2);
      a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: PINK_LAKE, arc: s.distance, side, shore: hw + 12 });
    }
    if (rng.next() < 0.4) {
      const [x, y, z] = at(hw + 7 + rng.range(0, 4));
      a.saltMounds.push({ x, y, z, rotY: rng.range(0, Math.PI), s: rng.range(0.8, 1.5) });
    }
    if (rng.next() < 0.22) {
      const [x, y, z] = at(hw + 16 + rng.range(0, 16));
      a.boats.push({ x, y: y + 0.12, z, rotY: rng.range(0, Math.PI * 2), color: rng.pick(BOAT_COLORS), s: rng.range(0.85, 1.1) });
    }
  } else {
    // The dune shore opposite.
    if (rng.next() < 0.18) {
      const [x, y, z] = at(hw + 8 + rng.range(0, 24));
      a.baobabs.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.7, 1.2) });
    }
    if (rng.next() < 0.25) {
      const base = hw + 20 + rng.range(0, 30);
      const r = rng.range(16, 32);
      const [x, y, z] = at(hillLat(hw, base, r));
      a.hills.push({ x, y, z, r, h: rng.range(4, 9), color: rng.pick(DAKAR_SAND) });
    }
  }
  // Salt harvesters working the pink-lake shore.
  if (side === LAKE_SIDE)
    scatterPeople({ peopleRng, at, a }, { band: [hw + 5, hw + 11], chance: 0.4, count: 2 });
};

export const DAKAR_ZONES = { medina, market, savanna, lacrose };
